import {
    Checkbox,
    DefaultButton,
    Dialog,
    DialogType,
    IDialogContentProps,
    Label,
    Layer,
    PrimaryButton,
    Stack,
    TextField,
    Image,
    IImageProps,
    ImageFit,
    ChoiceGroup,
    IChoiceGroupOption
} from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import React, { useEffect } from 'react';

import { ILinkAttachmentModalBoxState } from './LinkAttachmentModalBox.state';
import { useRecoilState } from 'recoil';
import { aLinkAttachmentModalValue } from './atom';

const LinkAttachmentModalBoxView: React.FC<ILinkAttachmentModalBoxState> = (props) => {
    const {
        toggleLinkAttachmentModalBox,
        cancelLinkAttachment,
        saveLinkAttachment,
        onLinkAttachmentValueChange,
        onLinkAttachmentPinnedChange,
        linkAttachmentModalValue,
        linkAttachmentItemId,
        linkAttachmentItemIsPinned
    } = props;
    const dialogContentMap = {
        youtubelinkmanager: {
            title: IvicosStrings.youtubeLinkAttachmentModalBoxTitle,
            subText: IvicosStrings.youtubeLinkAttachmentModalVideoPinUpperText
        },
        collaboardlinkmanager: {
            title: IvicosStrings.collaboardLinkAttachmentModalBoxTitle,
            subText: IvicosStrings.colleboardLinkAttachmentModalBoxSubText
        },
        conceptboardlinkmanager: {
            title: IvicosStrings.conceptboardLinkAttachmentModalBoxTitle,
            subText: IvicosStrings.conceptboardLinkAttachmentModalBoxSubText
        }
    };

    const dialogContentProps: IDialogContentProps = {
        type: DialogType.normal,
        styles: {
            title: {
                fontSize: 22,
                fontWeight: 900
            }
        },
        title: dialogContentMap[linkAttachmentItemId]?.title || IvicosStrings.linkAttachmentModalBoxTitle,
        closeButtonAriaLabel: IvicosStrings.closeActionTitle,
        subText: dialogContentMap[linkAttachmentItemId]?.subText || IvicosStrings.linkAttachmentModalBoxSubText
    };

    // const getVideoId= React.useCallback((url:string) => {
    //     const regex = RegExp(/(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm);
    //     return 'AMTjJAk33Nc';
    // },[])
    // const avatarURL = linkAttachmentItemId==='youtubelinkmanager' && linkAttachmentModalValue ? `https://img.youtube.com/vi/${youtubeId}/0.jpg` : '';
    const imageProps: IImageProps = {
        imageFit: ImageFit.cover,
        height: 150,
        styles: (props) => ({ root: { border: '1px solid ' + props.theme.palette.neutralSecondary } })
    };

    const linkAddressTextFieldComponent = (
        <Stack aria-label="link-attachment-modal-box-text-field">
            <TextField placeholder="https://..." type="text" onChange={onLinkAttachmentValueChange} />
        </Stack>
    );

    const youtubeAttachmentImageComponent = (
        <Stack aria-label="link-attachment-modal-box-is-video-thumbnail">
            <Image {...imageProps} src={'https://img.youtube.com/vi/' + linkAttachmentModalValue + '/0.jpg'} alt="Campus logo"></Image>
        </Stack>
    );

    const youtubeAttachmentLinkPinningComponent = (
        <Stack aria-label="link-attachment-modal-box-is-pinned-checkbox" tokens={{ childrenGap: 16 }}>
            <Label>{IvicosStrings.youtubeLinkAttachmentModalVideoPinUpperText}</Label>
            <Checkbox
                checked={linkAttachmentItemIsPinned}
                onChange={onLinkAttachmentPinnedChange}
                className="show-expired-invitations-button"
                label={IvicosStrings.youtubeLinkAttachmentModalVideoPinText}
            />
        </Stack>
    );

    const conceptBoardLinkTypeChoices: IChoiceGroupOption[] = [
        { key: 'createNewBoard', text: IvicosStrings.conceptboardLinkAttachmentCreateNewText },
        { key: 'addExistingBoard', text: IvicosStrings.conceptboardLinkAttachmentAddExistingText }
    ];

    const [conceptBoardLinkTypeChoice, setConceptBoardLinkTypeChoice] = React.useState<string>('createNewBoard');
    const [, setLinkAttachmentModalValue] = useRecoilState(aLinkAttachmentModalValue);

    useEffect(() => {
        const isNewConceptBoard = linkAttachmentItemId === 'conceptboardlinkmanager' && conceptBoardLinkTypeChoice === 'createNewBoard';
        if (isNewConceptBoard) setLinkAttachmentModalValue('https://conceptboard.com/new');
    }, [linkAttachmentItemId]);

    const conceptBoardLinkTypeChoiceGroup = (
        <ChoiceGroup
            defaultSelectedKey={conceptBoardLinkTypeChoice}
            options={conceptBoardLinkTypeChoices}
            onChange={(e, option) => {
                e?.preventDefault;
                setConceptBoardLinkTypeChoice(option ? option.key : 'createNewBoard');
                const isAddingExistingBoard = option?.key === 'addExistingBoard';
                if (!isAddingExistingBoard) setLinkAttachmentModalValue('https://conceptboard.com/new');
                else setLinkAttachmentModalValue('');
            }}
            label={IvicosStrings.trialPeriodExpiredChoiceTitle}
            aria-label="remove-org-choices"
            required
        />
    );

    const saveButtonText =
        linkAttachmentItemId !== 'conceptboardlinkmanager'
            ? IvicosStrings.saveActionTitle
            : conceptBoardLinkTypeChoice === 'createNewBoard'
            ? IvicosStrings.conceptboardSaveButtonCreate
            : IvicosStrings.conceptboardSaveButtonSaveToList;

    return (
        <Stack aria-label="link-attachment-modal-box">
            <Layer>
                <Dialog
                    modalProps={{ isBlocking: true, styles: { main: { width: '70% !important', minWidth: '280px !important', maxWidth: '400px !important' } } }}
                    hidden={!toggleLinkAttachmentModalBox}
                    onDismiss={cancelLinkAttachment}
                    dialogContentProps={dialogContentProps}
                >
                    <Stack tokens={{ childrenGap: 24 }}>
                        {linkAttachmentItemId === 'conceptboardlinkmanager' && conceptBoardLinkTypeChoiceGroup}
                        {(linkAttachmentItemId !== 'conceptboardlinkmanager' ||
                            !(linkAttachmentItemId === 'conceptboardlinkmanager' && conceptBoardLinkTypeChoice === 'createNewBoard')) &&
                            linkAddressTextFieldComponent}
                        {linkAttachmentItemId === 'youtubelinkmanager' && linkAttachmentModalValue !== '' && youtubeAttachmentImageComponent}
                        {linkAttachmentItemId === 'youtubelinkmanager' && youtubeAttachmentLinkPinningComponent}
                        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 16 }}>
                            <DefaultButton
                                aria-label="link-attachment-modal-box-cancel-button"
                                onClick={cancelLinkAttachment}
                                text={IvicosStrings.cancelActionTitle}
                            />
                            <PrimaryButton
                                aria-label="link-attachment-modal-box-save-button"
                                onClick={saveLinkAttachment}
                                text={saveButtonText}
                                disabled={
                                    linkAttachmentModalValue.length < 1
                                    // ||
                                    // !(linkAttachmentItemId === 'conceptboardlinkmanager' && conceptBoardLinkTypeChoice === 'createNewBoard')
                                }
                            />
                        </Stack>
                    </Stack>
                </Dialog>
            </Layer>
        </Stack>
    );
};

export default LinkAttachmentModalBoxView;
